import $axios from '@/backend.js'
import { sortArray, isDistributedInstance, isAdminRole, isMasterOrDistributedInstance, isMasterInstance } from '@/utils'
import { enumsData } from '@/mixins/enums'
import router from '@/router'
import { DateTime } from 'luxon'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as applicationErrors from '@/modules/application/errors'
import * as filesStorageErrors from '@/modules/files/errors'
import * as instanceErrors from '@/modules/instance/errors'
import * as invitationsErrors from '@/mixins/errors/invitations'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as snapshotErrors from '@/modules/snapshot/errors/snapshots'
import * as spaceErrors from '@/modules/space/errors'
import * as tablesErrors from '@/modules/snapshot/errors/tables'

const { getBackendErrorMessage } = createBackendErrorHandler(
    applicationErrors,
    filesStorageErrors,
    instanceErrors,
    invitationsErrors,
    lifecycleErrors,
    permissionsErrors,
    snapshotErrors,
    spaceErrors,
    tablesErrors
)

const getDefaultState = () => {
    return {
        currentSpace: null,
        fetchingCurrentSpace: false,
        spaceInstances: [],
        spaceUsers: [],
        spaceBundles: [],
        fetchingSpaceBundles: false,
        spaceInvitations: [],
        distributedInstanceId: null,
        fetchingInstances: false,
        invitationFromSnapshotView: false,
        fetchingSpaceUsers: null,
        fetchingSpaceInvitations: null,
        mappedResources: null,
        fetchingApplications: false,
        applicationFamilies: [],
        applicationVersions: [],
        masterInstance: null,
        distributedInstance: null,
        otherInstances: [],
        studentQuotaCurrent: null,
        studentQuotaFetchingCurrent: false,
        studentQuotaCurrentError: null,
        studentQuotaCurrentErrorMsg: '',
        studentQuotaUsages: [],
        studentQuotaFetchingUsages: false,
        studentQuotaUsagesError: null,
        studentQuotaUsagesErrorMsg: '',
        studentQuotaHistory: [],
        studentQuotaFetchingHistory: false,
        studentQuotaHistoryError: null,
        studentQuotaHistoryErrorMsg: '',
        studentQuotaLastEndDate: ''
    }
}
const spaceStore = {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        setCurrentSpace(state, space) {
            state.currentSpace = space
        },
        resetSpaceState(state) {
            Object.assign(state, getDefaultState())
        },
        setMasterInstance(state, instance) {
            state.masterInstance = instance
        },
        setDistributedInstance(state, instance) {
            state.distributedInstance = instance
        },
        setOtherInstances(state, instances) {
            state.otherInstances = instances
        },
        setInstances(state, instances) {
            state.spaceInstances = instances
        },
        setDistributedInstanceId(state, iid) {
            state.distributedInstanceId = iid
        },
        setUsers(state, users) {
            state.spaceUsers = users
        },
        setSpaceInvitations(state, invitations) {
            state.spaceInvitations = invitations
        },
        setSpaceBundles(state, bundles) {
            state.spaceBundles = bundles
        },
        setFetching(state, { fetchingObject, fetchingStatus }) {
            if (fetchingObject === 'instances') {
                state.fetchingInstances = fetchingStatus
            } else if (fetchingObject === 'currentSpace') {
                state.fetchingCurrentSpace = fetchingStatus
            } else if (fetchingObject === 'spaceUsers') {
                state.fetchingSpaceUsers = fetchingStatus
            } else if (fetchingObject === 'spaceInvitations') {
                state.fetchingSpaceInvitations = fetchingStatus
            } else if (fetchingObject === 'spaceBundles') {
                state.fetchingSpaceBundles = fetchingStatus
            }
        },
        setMappedResources(state, data) {
            state.mappedResources = data
        },
        setApplicationFamilies(state, families) {
            state.applicationFamilies = families
        },
        setApplicationVersions(state, versions) {
            state.applicationVersions = versions
        },
        setFetchingApplications(state, val) {
            state.fetchingApplications = val
        },
        setStudentQuotaCurrent(state, quota) {
            state.studentQuotaCurrent = quota
        },
        setStudentQuotaCurrentFetching(state, fetching) {
            state.studentQuotaFetchingCurrent = fetching
        },
        setStudentQuotaCurrentError(state, { error, errorMsg }) {
            state.studentQuotaCurrentError = error
            state.studentQuotaCurrentErrorMsg = errorMsg
        },
        setStudentQuotaUsages(state, usages) {
            state.studentQuotaUsages = usages
        },
        setStudentQuotaUsagesFetching(state, fetching) {
            state.studentQuotaFetchingUsages = fetching
        },
        setStudentQuotaUsagesError(state, { error, errorMsg }) {
            state.studentQuotaUsagesError = error
            state.studentQuotaUsagesErrorMsg = errorMsg
        },
        setStudentQuotaHistory(state, history) {
            state.studentQuotaHistory = history
        },
        setStudentQuotaHistoryFetching(state, fetching) {
            state.studentQuotaFetchingHistory = fetching
        },
        setStudentQuotaHistoryError(state, { error, errorMsg }) {
            state.studentQuotaHistoryError = error
            state.studentQuotaHistoryErrorMsg = errorMsg
        },
        setStudentQuotaLastEndDate(state, lastEndDate) {
            state.studentQuotaLastEndDate = lastEndDate
        }
    },
    actions: {
        resetSpaceState(context) {
            context.commit('resetSpaceState')
        },
        setDistributedInstanceId(context, instances) {
            const distributedInstance = instances.find(instance => isDistributedInstance(instance.short_id))
            context.commit('setDistributedInstanceId', distributedInstance ? distributedInstance.iid : null)
        },
        fetchSpaceInstances(context, payload) {
            const sid = typeof payload === 'object' ? payload.sid : payload
            const spaceType = typeof payload === 'object' && payload.spaceType !== undefined ? payload.spaceType : context.getters.currentSpaceType
            if (spaceType === null || spaceType === undefined) return
            context.commit('setFetching', { fetchingObject: 'instances', fetchingStatus: true })
            return $axios
                .get(`spaces/${sid}/instances`)
                .then(({ data }) => {
                    const master = data.filter(instance => isMasterInstance(instance.long_id))
                    const distributed = data.filter(instance => isDistributedInstance(instance.long_id))
                    const nonMasterAndDistributed = data.filter(instance => !isMasterOrDistributedInstance(instance.long_id))
                    const sortedNonMasterAndDistributed = sortArray(nonMasterAndDistributed, 'long_id', 'ascending', true)

                    context.commit('setMasterInstance', master[0])
                    context.commit('setDistributedInstance', distributed[0])
                    context.commit('setDistributedInstanceId', distributed[0] ? distributed[0].iid : null)
                    context.commit('setOtherInstances', sortedNonMasterAndDistributed)

                    let finalInstanceList = null
                    if (spaceType === enumsData.data().spaceTypes.VENDOR_SPACE || spaceType === enumsData.data().spaceTypes.RESEARCH_SPACE) {
                        finalInstanceList = master.concat(sortedNonMasterAndDistributed)
                    } else {
                        finalInstanceList = master.concat(distributed).concat(sortedNonMasterAndDistributed)
                    }

                    context.commit('setInstances', finalInstanceList)
                    context.dispatch('setDistributedInstanceId', finalInstanceList)
                })
                .finally(() => {
                    context.commit('setFetching', { fetchingObject: 'instances', fetchingStatus: false })
                })
        },
        fetchSpaceUsers(context, sid) {
            context.commit('setFetching', { fetchingObject: 'spaceUsers', fetchingStatus: true })
            $axios
                .get(`/spaces/${sid}/list_all_users`)
                .then(function (response) {
                    context.commit('setUsers', response.data)
                })
                .finally(() => {
                    context.commit('setFetching', { fetchingObject: 'spaceUsers', fetchingStatus: false })
                })
        },
        async fetchCurrentSpace(context) {
            context.commit('setFetching', { fetchingObject: 'currentSpace', fetchingStatus: true })
            try {
                const { data } = await $axios.get(`/spaces/${router.currentRoute.params.sid}`)
                context.commit('setCurrentSpace', data)
                return data
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, 'Failed to get space data!')
                if (snackBarText) {
                    context.commit(
                        'showSnackBar',
                        {
                            snackBarText,
                            snackBarTimeout: 5000,
                            snackBarIcon: 'error'
                        },
                        { root: true }
                    )
                }
                return error
            } finally {
                context.commit('setFetching', { fetchingObject: 'currentSpace', fetchingStatus: false })
            }
        },
        fetchSpaceBundles(context, sid) {
            context.commit('setFetching', { fetchingObject: 'spaceBundles', fetchingStatus: true })
            $axios
                .get(`/space/${sid}/bundles?handins=true`)
                .then(function (response) {
                    context.commit('setSpaceBundles', response.data)
                })
                .finally(() => {
                    context.commit('setFetching', { fetchingObject: 'spaceBundles', fetchingStatus: false })
                })
        },
        fetchSpaceInvitations(context, sid) {
            context.commit('setFetching', { fetchingObject: 'spaceInvitations', fetchingStatus: true })
            $axios
                .post(`/spaces/${sid}/invitations_v2`, { children_instance_invitations: true })
                .then(function (response) {
                    context.commit('setSpaceInvitations', response.data)
                })
                .finally(() => {
                    context.commit('setFetching', { fetchingObject: 'spaceInvitations', fetchingStatus: false })
                })
        },
        fetchMappedResources(context, sid) {
            return $axios.get(`/spaces/${sid}/resource_allocation`).then(response => {
                context.commit('setMappedResources', response.data)
            })
        },
        async fetchApplications(context, sid) {
            context.commit('setFetchingApplications', true)
            try {
                const fetchingFamilies = $axios.get(`/spaces/${sid}/image_families`)
                const fetchingVersions = $axios.get(`/spaces/${sid}/images_v2`)

                const [families, versions] = await Promise.all([fetchingFamilies, fetchingVersions])
                context.commit('setApplicationFamilies', families.data)
                context.commit('setApplicationVersions', versions.data)
            } catch (error) {
                console.log(error)
            } finally {
                context.commit('setFetchingApplications', false)
            }
        },
        fetchStudentQuotaCurrent(context, sidParam) {
            const sid = sidParam || router.currentRoute.params.sid
            if (!sid) {
                return Promise.resolve()
            }

            context.commit('setStudentQuotaCurrentFetching', true)
            context.commit('setStudentQuotaCurrentError', { error: null, errorMsg: '' })

            return $axios
                .get(`/space_credit_limit/${sid}/current`)
                .then(({ data }) => {
                    context.commit('setStudentQuotaCurrent', data)
                })
                .catch(error => {
                    context.commit('setStudentQuotaCurrent', null)

                    let errorMsg = getBackendErrorMessage(error, error.response?.data?.debug || '') || 'There was an error fetching quota information'
                    if (error?.response?.data?.code === 'space_credit_limit_not_found') {
                        errorMsg = 'Space credit limit not found'
                    }
                    if (error?.response?.data?.code === 'space_credit_limit_end_date_invalid') {
                        errorMsg = 'Invalid end date for space credit limit'
                    }

                    context.commit('setStudentQuotaCurrentError', { error, errorMsg })
                })
                .finally(() => {
                    context.commit('setStudentQuotaCurrentFetching', false)
                })
        },
        fetchStudentQuotaUsages(context, sidParam) {
            const sid = sidParam || router.currentRoute.params.sid
            if (!sid) {
                return Promise.resolve()
            }

            context.commit('setStudentQuotaUsagesFetching', true)
            context.commit('setStudentQuotaUsagesError', { error: null, errorMsg: '' })

            return $axios
                .get(`/space_credit_limit/usage/${sid}/instances`)
                .then(({ data }) => {
                    context.commit('setStudentQuotaUsages', data.instance_credit_usage || [])
                })
                .catch(error => {
                    context.commit('setStudentQuotaUsages', [])
                    context.commit('setStudentQuotaUsagesError', {
                        error,
                        errorMsg: getBackendErrorMessage(error, error.response?.data?.debug || '') || 'There was an error fetching instance usage information'
                    })
                })
                .finally(() => {
                    context.commit('setStudentQuotaUsagesFetching', false)
                })
        },
        fetchStudentQuotaHistory(context, sidParam) {
            const sid = sidParam || router.currentRoute.params.sid
            if (!sid) {
                return Promise.resolve()
            }

            context.commit('setStudentQuotaHistoryFetching', true)
            context.commit('setStudentQuotaHistoryError', { error: null, errorMsg: '' })

            return $axios
                .get(`/space_credit_limit/${sid}/history`)
                .then(({ data }) => {
                    const now = DateTime.now().startOf('day')
                    const parsedLastEndDate = DateTime.fromISO(data?.last_end_date || '')
                    context.commit('setStudentQuotaLastEndDate', parsedLastEndDate.isValid ? parsedLastEndDate.toISODate() || '' : '')

                    if (Array.isArray(data?.history)) {
                        const mappedHistory = data.history.map(schedule => {
                            const dt = DateTime.fromISO(schedule.end_date || '')
                            return {
                                ...schedule,
                                past: dt.isValid ? dt < now : false
                            }
                        })
                        context.commit('setStudentQuotaHistory', mappedHistory)
                    } else {
                        context.commit('setStudentQuotaHistory', [])
                    }
                })
                .catch(error => {
                    context.commit('setStudentQuotaHistory', [])
                    context.commit('setStudentQuotaLastEndDate', '')
                    context.commit('setStudentQuotaHistoryError', {
                        error,
                        errorMsg: getBackendErrorMessage(error, error.response?.data?.debug || '') || 'There was an error fetching credit quota schedule'
                    })
                })
                .finally(() => {
                    context.commit('setStudentQuotaHistoryFetching', false)
                })
        },
        refreshStudentQuotaData(context, sidParam) {
            const sid = sidParam || router.currentRoute.params.sid
            return Promise.all([
                context.dispatch('fetchStudentQuotaCurrent', sid),
                context.dispatch('fetchStudentQuotaUsages', sid),
                context.dispatch('fetchStudentQuotaHistory', sid)
            ])
        }
    },
    getters: {
        spaceLongNameById: (state, getters, rootState) => sid => {
            if (sid !== undefined && sid !== null) {
                const space = rootState.orgStore.spacesWithPrimarySnapshots.find(space => space.sid.toString() === sid.toString())
                if (space) {
                    return space.space_long_id
                }
            }
            return null
        },
        currentSpaceName: (state, getters, rootState) => {
            const sid = rootState.route?.params?.sid
            if (sid === undefined || sid === null) {
                return null
            }
            return state.currentSpace?.long_id
        },
        spaceDescriptionById: (state, getters, rootState) => sid => {
            if (sid !== undefined && sid !== null) {
                const space = rootState.orgStore.spacesWithPrimarySnapshots.find(space => space.sid.toString() === sid.toString())
                if (space) {
                    return space.space_description
                }
            }
            return 'No description available.'
        },
        spaceArchiveTimestampById: (state, getters, rootState) => sid => {
            if (sid !== undefined && sid !== null) {
                const space = rootState.orgStore.spacesWithPrimarySnapshots.find(space => space.sid.toString() === sid.toString())
                if (space) {
                    return space.space_archival_timestamp
                }
            }
            return null
        },
        spaceArchivalDateById: (state, getters, rootState) => sid => {
            if (sid !== undefined && sid !== null) {
                const space = rootState.orgStore.spacesWithPrimarySnapshots.find(space => space.sid.toString() === sid.toString())
                if (space) {
                    return space.space_archive_by_date
                }
            }
            return null
        },
        spaceCreationDateById: (state, getters, rootState) => sid => {
            if (sid !== undefined && sid !== null) {
                const space = rootState.orgStore.spacesWithPrimarySnapshots.find(space => space.sid.toString() === sid.toString())
                if (space) {
                    return space.space_creation_timestamp
                }
            }
            return null
        },
        spaceArchivalDate: (state, getters, rootState) => {
            return getters.spaceArchivalDateById(rootState.route.params.sid)
        },
        spaceCreationDate: (state, getters, rootState) => {
            return getters.spaceCreationDateById(rootState.route.params.sid)
        },
        isSpaceArchived: state => {
            return Boolean(state.currentSpace?.archival_timestamp || state.currentSpace?.space_archival_timestamp)
        },
        isSpaceAdmin: (state, getters, rootState) => {
            if (rootState.route.params.sid !== undefined && rootState.orgStore.spacesWithPrimarySnapshots) {
                const currentSpace = rootState.orgStore.spacesWithPrimarySnapshots.find(space => space.sid.toString() === rootState.route.params.sid.toString())
                if (currentSpace && isAdminRole(currentSpace.space_role)) {
                    return true
                }
            }
            return false
        },
        currentSpaceType: (state, getters, rootState) => {
            if (rootState.route.params.sid !== undefined && rootState.orgStore.spacesWithPrimarySnapshots) {
                const space = rootState.orgStore.spacesWithPrimarySnapshots.find(space => space.sid.toString() === rootState.route.params.sid.toString())
                if (space) {
                    return space.space_type
                }
            }
            return null
        },
        currentSpaceVisibility: (state, getters, rootState) => {
            if (rootState.route.params.sid !== undefined && rootState.orgStore.spacesWithPrimarySnapshots) {
                const space = rootState.orgStore.spacesWithPrimarySnapshots.find(space => space.sid.toString() === rootState.route.params.sid.toString())
                if (space) {
                    return space.space_visibility_type
                }
            }
            return null
        },
        currentSpaceData: (state, getters, rootState) => {
            const activeSpaceId = rootState.route.params.sid
            if (!activeSpaceId) {
                return null
            }

            if (state.currentSpace?.sid?.toString() === activeSpaceId.toString()) {
                return state.currentSpace
            }

            return rootState.orgStore.spacesWithPrimarySnapshots?.find(space => space.sid.toString() === activeSpaceId.toString()) || null
        },
        assignmentsExist(state) {
            const distributionBundles = state.spaceBundles.filter(bundle => bundle.bundle_type_name === enumsData.data().distributionReasons.ASSIGNMENT)
            if (distributionBundles.length) {
                return true
            }
            return false
        },
        privateInstances(state) {
            return state.spaceInstances.filter(instance => !isMasterOrDistributedInstance(instance.long_id))
        },
        isTrialSpace(state, getters) {
            return getters.currentSpaceData?.snapshot_location === 'AZFS_PREMIUM'
        },
        applicationTags(state) {
            const allTags = {}
            try {
                state.applicationVersions.forEach(version => {
                    if (version.tags == null) return
                    for (const [tagType, tagNames] of Object.entries(version.tags)) {
                        const tagNamesArr = Array.isArray(tagNames) ? tagNames : [tagNames]
                        tagNamesArr.forEach(tagName => {
                            if (!allTags[`${tagType}:${tagName}`]) allTags[`${tagType}:${tagName}`] = []
                            allTags[`${tagType}:${tagName}`].push(version.imid)
                        })
                    }
                })
            } catch (err) {
                console.warn('app image configuration error:', err.message)
            }
            return allTags
        },
        isSpaceRested(state, getters, rootState) {
            return state.currentSpace?.resting_state === enumsData.data().spaceStatus.RESTED
        },
        isSpacePreResting(state, getters, rootState) {
            return state.currentSpace?.resting_state === enumsData.data().spaceStatus.PRE_RESTING
        },
        isSpaceResting(state, getters, rootState) {
            return state.currentSpace?.resting_state === enumsData.data().spaceStatus.RESTING
        },
        isSpaceWaking(state, getters, rootState) {
            return state.currentSpace?.resting_state === enumsData.data().spaceStatus.WAKING
        },
        isSpaceInAnyRestState(state, getters, rootState) {
            return getters.isSpaceRested || getters.isSpacePreResting || getters.isSpaceWaking || getters.isSpaceResting
        },
        isSpaceInAnyRestStateExceptPreResting(state, getters, rootState) {
            return getters.isSpaceRested || getters.isSpaceWaking || getters.isSpaceResting
        }
    }
}
export default spaceStore
